'use client';

import { createDebug } from '@shared/debug';
import { useClientConfig } from '@shared/utils';
import { useEffect, useState } from 'react';
import type { AddressLookupProps } from '../types';
import { AddressLookup } from './AddressLookup';

/**
 * Wrapper component that handles configuration availability for AddressLookup
 */
function AddressLookupWithConfig(props: AddressLookupProps) {
  const debug = createDebug({ isEnabled: false });
  const [isConfigReady, setIsConfigReady] = useState(false);
  const [configError, setConfigError] = useState<Error | null>(null);

  // Always call useClientConfig at component level
  const config = useClientConfig();

  // Check if configuration is available
  useEffect(() => {
    try {
      if (config?.addressLookup?.apiKey && config?.addressLookup?.baseUrl) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsConfigReady(true);
        setConfigError(null);
      } else {
        setConfigError(new Error('Address lookup configuration incomplete'));
      }
    } catch (error) {
      setConfigError(error instanceof Error ? error : new Error(String(error)));
      debug.warn('AddressLookup: Configuration not available:', error);
    }
  }, [config]);

  // If configuration is not ready, show a disabled input
  if (!isConfigReady) {
    return (
      <AddressLookup
        {...props}
        disabled={true}
        helpMessage={configError ? 'Address lookup temporarily unavailable' : 'Loading...'}
        helpMessageType={configError ? 'error' : 'info'}
      />
    );
  }

  // Configuration is ready, render the full component
  return <AddressLookup {...props} />;
}

export { AddressLookupWithConfig };
